import React, { Suspense, useEffect, useLayoutEffect } from "react";
import './ProductSearch.css';
import {
  Box,
  TextField,
  Icon,
  InlineStack,
  Button,
  Popover,
  OptionList,
  Text,
  RadioButton,
  Page,
  Scrollable,
  Spinner,
  BlockStack,
  Banner
} from '@shopify/polaris';
import { useState, useCallback, useRef } from 'react';
import {
  SearchIcon, SortIcon, ArrowUpIcon, ArrowDownIcon
} from '@shopify/polaris-icons';
import CardList from "./CardList.react.js";
import CascadingMenu from "../CommonComponents/CascadingMenu.react.js";
import { categoryList } from "../utils/CategoryList.js";
import { gql, useMutation } from "@apollo/client";
import GetCurrencySymbol from '../utils/GetCurrencySymbol.js';
import { currencyList, countryList, majorCurrencyList } from "../utils/CurrencyList.js";
import { useAppBridge } from '@shopify/app-bridge-react';
import { Context } from "../Context/Context.js";
import { AppName } from "../ShopifyConstants.js";
import { i18 } from "./ProductSearchUtil.js";
import { i18 as translationCountry } from "../utils/CountryUtil.js";
import { openExternalUrl } from '../utils/urlUtils.js';
import { useNavigate, useSearchParams } from "react-router-dom";
import GetStartedGuide from "../CommonComponents/GetStartedGuide.react.js";

const ImportProductByURL = React.lazy(() => import("../ImportProductByURL/ImportProductByURL.react.js"));

const SET_SHIP_TO_COUNTRY_AND_SETTING_FOURPX_SHIPPING_OPTIONS = gql`
  mutation setShipToCountryAndSettingFourPXShippingOptions($shipToCountry: String) {
    setShipToCountryAndSettingFourPXShippingOptions(shipToCountry: $shipToCountry){
      status
      message
    }
  }
`;

const SAVE_SETTING_CURRENCY = gql`
  mutation saveSettingProduct(
    $currency: String
  ) {
    saveSettingProduct(
      currency: $currency
    ) {
      status
      message
    }
  }
`;

const ADOPT_SHOPIFY_PRODUCT = gql`
  mutation AdoptShopifyProductForSupplierSearch($shopifyProductID: String!) {
    adoptShopifyProduct(shopifyProductID: $shopifyProductID) {
      status
      productSchemaID
      alreadyManaged
      message
      errorMessage
    }
  }
`;

function ProductSearch() {
  const shopify = useAppBridge();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const adminProductSearchIntent = searchParams.get("intent");
  const smartRecommendationSearchIntent = adminProductSearchIntent === "trend_product_search";
  const smartRecommendationSearchKeywords = String(searchParams.get("searchKeywords") || searchParams.get("keyword") || "").trim();
  const smartRecommendationProvider = String(searchParams.get("provider") || "").trim();
  const smartRecommendationCountry = String(searchParams.get("country") || "").trim();
  const shouldResumePendingImport = searchParams.get("resumeImport") === "1";
  const initialWidth = getInitialWidthRowNumber();
  const [cardWidth, setCardWidth] = useState(initialWidth.currentWidth);
  const [rowsNumber, setRowsNumber] = useState(initialWidth.currentRowsNumber);
  const [titleValue, setTitleValue] = useState("");
  const [currentTitle, setCurrentTitle] = useState("");
  const [minPriceRange, setMinPriceRange] = useState();
  const [maxPriceRange, setMaxPriceRange] = useState();
  const [currentMinPriceRange, setCurrentMinPriceRange] = useState();
  const [currentmaxPriceRange, setCurrentMaxPriceRange] = useState();
  const [sortSelected, setSortSelected] = useState([]);
  const [priceRangeSelected, setPriceRangeSelected] = useState([]);
  const [currentCategory, setCurrentCategory] = useState("Product Category");
  const [currentCategoryValue, setCurrentCategoryValue] = useState("all");
  const [currentPriceRange, setCurrentPriceRange] = useState("Price Range");
  const [currentPriceRangeOption, setCurrentPriceRangeOption] = useState("Price Range");
  const [currentSort, setCurrentSort] = useState("Default");
  const [currentSortRule, setCurrentSortRule] = useState("normal");
  const [currencySymbol, setCurrencySymbol] = useState(GetCurrencySymbol(localStorage.getItem('currency')));
  const [isShowImportURLComponent, setIsShowImportURLComponent] = useState(false);
  const [adminSupplierIntentNotice, setAdminSupplierIntentNotice] = useState(false);
  const [smartRecommendationSearchNotice, setSmartRecommendationSearchNotice] = useState(false);
  const [sidekickImportNotice, setSidekickImportNotice] = useState("");
  const [sidekickSupplierUrl, setSidekickSupplierUrl] = useState("");
  const productCardWrap = useRef(null);
  const searchInput = useRef(null);
  const [set_ship_to_country_and_setting_fourPX_shipping_options] = useMutation(SET_SHIP_TO_COUNTRY_AND_SETTING_FOURPX_SHIPPING_OPTIONS);
  const [currentCountry, setCurrentCountry] = useState(getCountryOption(localStorage.getItem('shipToCountry')));
  const [countryActive, setCountryActive] = useState(false);
  const [countryLoading, setCountryLoading] = useState(false);
  const [save_setting_currency] = useMutation(SAVE_SETTING_CURRENCY);
  const [adoptShopifyProduct] = useMutation(ADOPT_SHOPIFY_PRODUCT);

  useEffect(() => {
    if (!shouldResumePendingImport) return;
    const pendingImportURL = localStorage.getItem("cartifindPendingImportURL") || "";
    const pendingImportAt = Number(localStorage.getItem("cartifindPendingImportAt") || 0);
    const pendingImportIsFresh = pendingImportAt > 0 && Date.now() - pendingImportAt < 24 * 60 * 60 * 1000;
    if (pendingImportURL && pendingImportIsFresh) {
      setIsShowImportURLComponent(true);
      shopify.toast.show(i18("Your product URL is ready. Select Import to continue."));
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("resumeImport");
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams, shopify, shouldResumePendingImport]);

  const toggleCountryActive = useCallback(() => setCountryActive((active) => !active), []);
  const countryActivator = (
    <Box className="page-secondary-button-wrap">
      <Button onClick={toggleCountryActive} size="large" disclosure variant="secondary">
        <Box style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Text>{i18("Ship to")}:&nbsp;</Text>
          {countryLoading ? <Spinner size="small" style={{ height: "20px" }} /> : <Text> {translationCountry(currentCountry)}</Text>}
        </Box>
      </Button>
    </Box>
  );
  const [countryInput, setCountryInput] = useState("");
  const handleCountryInputChange = useCallback(
    (value) => setCountryInput(value),
    [],
  );
  const [currentCurrency, setCurrentCurrency] = useState(localStorage.getItem('currency'));
  const [currencyActive, setCurrencyActive] = useState(false);

  const toggleCurrencyActive = useCallback(() => setCurrencyActive((active) => !active), []);
  const [currencyLoading, setCurrencyLoading] = useState(false);
  const currencyActivator = (
    <Box style={{ width: "130px" }} className="page-secondary-button-wrap">
      <Button onClick={toggleCurrencyActive} size="large" disclosure variant="secondary">
        <Box style={{ width: "87px", display: "flex", alignItems: "center", gap: "5px" }}>
          <Text>{i18("Currency")}: </Text>
          {currencyLoading ? <Spinner size="small" style={{ height: "20px" }} /> : <Text> {currentCurrency}</Text>}
        </Box>
      </Button>
    </Box>
  );
  const [currencyInput, setCurrencyInput] = useState("");
  const handleCurrenctInputChange = useCallback(
    (value) => setCurrencyInput(value),
    [],
  );

  useEffect(() => {
    if (adminProductSearchIntent !== "find_supplier") return;
    const shopifyProductId = String(searchParams.get("shopifyProductId") || "").trim();
    if (!shopifyProductId) {
      setIsShowImportURLComponent(true);
      setAdminSupplierIntentNotice(true);
      shopify.toast.show(i18("Shopify product context is missing. Import by URL will create a new Cartifind product."), { isError: true });
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("intent");
      nextParams.delete("source");
      setSearchParams(nextParams, { replace: true });
      return;
    }

    let active = true;
    shopify.loading(true);
    shopify.toast.show(i18("Connecting the Shopify product before finding suppliers."));
    adoptShopifyProduct({ variables: { shopifyProductID: shopifyProductId } })
      .then((response) => {
        if (!active) return;
        const result = response?.data?.adoptShopifyProduct;
        if (!result?.status || !result?.productSchemaID) {
          throw new Error(result?.errorMessage || result?.message || "Shopify product could not be connected.");
        }
        shopify.toast.show(i18("Shopify product connected. Starting supplier search."));
        navigate(`/myProducts/${result.productSchemaID}?intent=find_reliable_suppliers`, { replace: true });
      })
      .catch((error) => {
        if (!active) return;
        shopify.toast.show(i18(error?.message || "Shopify product could not be connected. No duplicate product was created."), { isError: true });
      })
      .finally(() => {
        if (active) shopify.loading(false);
      });
    return () => { active = false; };
  }, [adminProductSearchIntent, searchParams, setSearchParams, shopify, adoptShopifyProduct, navigate]);

  useEffect(() => {
    if (adminProductSearchIntent !== "sidekick_import") return;
    const sourceUrl = String(searchParams.get("sourceUrl") || "").trim();
    const keywords = String(searchParams.get("searchKeywords") || searchParams.get("keyword") || "").trim();
    const supplier = String(searchParams.get("provider") || "").trim();
    if (isCartifindSupplierPage(sourceUrl)) {
      setSidekickSupplierUrl(sourceUrl);
      setSidekickImportNotice(i18("Open the supplier page. The Cartifind button there imports this product."));
    } else if (keywords) {
      setSidekickSupplierUrl("");
      setTitleValue(keywords);
      setCurrentTitle(keywords);
      setSidekickImportNotice(i18("Open a supplier product. The Cartifind button on that page imports it."));
    } else {
      setSidekickSupplierUrl("");
      setSidekickImportNotice(i18("Paste an AliExpress, Temu, or 1688 product URL, then open that page to import it."));
    }
    if (supplier) {
      shopify.toast.show(`${i18("Supplier")}: ${supplier}`);
    }
    const nextParams = new URLSearchParams(searchParams);
    ["intent", "source", "sourceUrl", "searchKeywords", "keyword", "provider"].forEach((key) => nextParams.delete(key));
    setSearchParams(nextParams, { replace: true });
  }, [adminProductSearchIntent, searchParams, setSearchParams, shopify]);

  useEffect(() => {
    if (!smartRecommendationSearchIntent || !smartRecommendationSearchKeywords) return;
    setTitleValue(smartRecommendationSearchKeywords);
    setCurrentTitle(smartRecommendationSearchKeywords);
    setSmartRecommendationSearchNotice(true);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("intent");
    nextParams.delete("source");
    setSearchParams(nextParams, { replace: true });
  }, [smartRecommendationSearchIntent, smartRecommendationSearchKeywords, searchParams, setSearchParams]);

  const countryEffect = async (country) => {
    setCountryLoading(true);
    shopify.loading(true);
    const result = await set_ship_to_country_and_setting_fourPX_shipping_options({
      variables: {
        shipToCountry: country,
      },
    })
    setCountryLoading(false);
    if (result != null && result.data != null && result.data.setShipToCountryAndSettingFourPXShippingOptions != null && result.data.setShipToCountryAndSettingFourPXShippingOptions.status) {
      localStorage.setItem("shipToCountry", country);
      shopify.toast.show(i18("Ship to country is updated"));
    } else {
      shopify.toast.show(i18("Failed to update ship-to country"), { isError: true });
    }
    shopify.loading(false);
  }

  const currencyEffect = async (currency) => {
    setCurrencyLoading(true);
    shopify.loading(true);
    const result = await save_setting_currency({
      variables: {
        currency,
      },
    })
    setCurrencyLoading(false);
    shopify.loading(false);
    if (result != null && result.data != null && result.data.saveSettingProduct != null) {
      if (result.data.saveSettingProduct.status) {
        shopify.toast.show(i18("Currency is updated"));
        localStorage.setItem("currency", currency);
        setCurrencySymbol(GetCurrencySymbol(currency));
        setCurrentCurrency(currency);
      } else {
        shopify.toast.show(i18("Failed to update currency"), { isError: true });
      }
    } else {
      shopify.toast.show(i18("Currency fail to update"), { isError: true });
    }
  }

  const handleTitleChange = useCallback(
    (value) => {
      setTitleValue(value)
    },
    [],
  );
  const handleTitleBlur = useCallback(
    (event) => {
      setCurrentTitle(event.target.value)
    },
    [],
  );
  const handleMinPriceRangeChange = (value) => {
    if (isValidNumber(value)) {
      setMinPriceRange(value);
    }
  }
  const handleMaxPriceRangeChange = (value) => {
    if (isValidNumber(value)) {
      setMaxPriceRange(value);
    }
  }
  const handleMinPriceRangeBlur = (event) => {
    const formattedValue = formatNumber(event.target.value);
    setMinPriceRange(formattedValue);
    setCurrentMinPriceRange(formattedValue);
    if (formattedValue != null && formattedValue.length > 0 && maxPriceRange != null && maxPriceRange.length > 0) {
      if (parseFloat(formattedValue) > parseFloat(maxPriceRange)) {
        setMaxPriceRange(formattedValue);
        setCurrentMaxPriceRange(formattedValue);
        setCurrentPriceRangeOption(`${currencySymbol} ${formattedValue}`);
      } else {
        setCurrentPriceRangeOption(`${currencySymbol} ${formattedValue} ~ ${currencySymbol} ${maxPriceRange}`);
      }
    } else if (formattedValue != null && formattedValue.length > 0) {
      setCurrentPriceRangeOption(`${i18("Over")} ${currencySymbol} ${formattedValue}`);
    } else if (maxPriceRange != null && maxPriceRange.length > 0) {
      setCurrentPriceRangeOption(`${i18("Under")} ${currencySymbol} ${maxPriceRange}`);
    } else {
      setCurrentPriceRangeOption(i18(`Price Range`));
    }
  }
  const handleMaxPriceRangeBlur = (event) => {
    const formattedValue = formatNumber(event.target.value);
    setMaxPriceRange(formattedValue);
    setCurrentMaxPriceRange(formattedValue);
    if (minPriceRange != null && minPriceRange.length > 0 && formattedValue != null && formattedValue.length > 0) {
      if (parseFloat(formattedValue) < parseFloat(minPriceRange)) {
        setMinPriceRange(formattedValue);
        setCurrentMinPriceRange(formattedValue);
        setCurrentPriceRangeOption(`${currencySymbol} ${formattedValue}`);
      } else {
        setCurrentPriceRangeOption(`${currencySymbol} ${minPriceRange} ~ ${currencySymbol} ${formattedValue}`);
      }
    } else if (minPriceRange != null && minPriceRange.length > 0) {
      setCurrentPriceRangeOption(`${i18("over")} ${currencySymbol} ${minPriceRange}`);
    } else if (formattedValue != null && formattedValue.length > 0) {
      setCurrentPriceRangeOption(`${i18("under")} ${currencySymbol} ${formattedValue}`);
    } else {
      setCurrentPriceRangeOption(i18(`Price Range`));
    }
  }

  const [sortActive, setSortActive] = useState(false);

  const toggleSortActive = useCallback(() => setSortActive((active) => !active), []);
  const sortActivator = (
    <Box style={{ width: "77px", display: "flex", justifyContent: "flex-end" }}> <Box style={{ width: "72px" }}><Button fullWidth icon={currentSortRule === "normal" ? SortIcon : (currentSortRule === "growth" ? ArrowUpIcon : ArrowDownIcon)} onClick={toggleSortActive} ><Text variant="bodyMd">{i18(sortSelected != null && sortSelected.length > 0 && sortSelected[0] != "Default" ? sortSelected[0].slice(0, 5) : "Sort")}</Text></Button></Box></Box>
  );
  const [productCategoryActive, setProductCategoryActive] = useState(false);
  const toggleProductCategoryActive = useCallback(() => setProductCategoryActive((active) => !active), []);
  const closeProductCategoryActive = useCallback(() => setProductCategoryActive(false), []);
  const chooseCategory = useCallback((value, option) => {
    setCurrentCategoryValue(value);
    setCurrentCategory(option !== "All Categories" ? option : "Product Category");
  }, []);
  const productCategoryActivator = (
    <Button onClick={toggleProductCategoryActive} disclosure><Text variant="bodyMd">{i18(currentCategory)}</Text></Button>
  );
  const [priceRangeActive, setPriceRangeActive] = useState(false);

  const priceRangeToggleActive = useCallback(() => setPriceRangeActive((active) => !active), []);
  const priceRangeActivator = (
    <Button onClick={priceRangeToggleActive} disclosure>
      <Box>
        <Text variant="bodyMd">{i18(currentPriceRange != "clear" ? (currentPriceRange != "custom" ? currentPriceRange : currentPriceRangeOption) : "Price Range")}</Text>
      </Box>
    </Button>
  );

  const handleSearchInputKeyDown = useCallback((event) => {
    const { key } = event;
    if (key === 'Enter') {
      setCurrentTitle(event.target.value);
    }
  }, []);  // Dependency array to avoid unnecessary recreations

  const handleResize = useCallback(() => {
    if (productCardWrap.current) {
      const allWidth = productCardWrap.current.offsetWidth;
      // Ignore the pre-layout 0px read. A later correction would reflow the grid after paint.
      if (allWidth < 270) return;
      let currentWidth = "270px";
      let currentRowsNumber = 5;
      switch (true) {
        case allWidth > 1670:
          // 一排六张
          currentWidth = (allWidth - 50) / 6 - 1 + "px";
          currentRowsNumber = 6;
          break;
        case allWidth > 1390:
          // 一排五张
          currentWidth = (allWidth - 40) / 5 - 1 + "px";
          currentRowsNumber = 5;
          break;
        case allWidth > 1110:
          // 一排四张
          currentWidth = (allWidth - 30) / 4 - 1 + "px";
          currentRowsNumber = 4;
          break;
        case allWidth > 830:
          // 一排三张
          currentWidth = (allWidth - 20) / 3 - 1 + "px";
          currentRowsNumber = 3;
          break;
        case allWidth > 550:
          // 一排两张
          currentWidth = (allWidth - 10) / 2 - 1 + "px";
          currentRowsNumber = 2;
          break;
        case allWidth > 270:
          // 一排一张
          currentWidth = allWidth + "px";
          currentRowsNumber = 1;
          break;
      }
      if (cardWidth != currentWidth) {
        setCardWidth(currentWidth);
      }
      if (rowsNumber != currentRowsNumber) {
        setRowsNumber(currentRowsNumber);
      }
    }
  }, [productCardWrap, cardWidth, rowsNumber]);

  const { state } = React.useContext(Context);
  const {
    sendLog,
    hasChromeExtension,
  } = state;

  useEffect(() => {
    if (sendLog != null) {
      sendLog("PRODUCT_SEARCH_TAB_CLICK");
    }
  }, [sendLog])

  useLayoutEffect(() => {
    handleResize();
  }, [handleResize]);

  useEffect(() => {
    const debouncedResize = debounce(handleResize, 200);
    window.addEventListener("resize", debouncedResize, true);
    searchInput != null && searchInput.current != null && searchInput.current.addEventListener('keydown', handleSearchInputKeyDown);

    // 清理副作用
    return () => {
      window.removeEventListener("resize", debouncedResize, true);
      searchInput != null && searchInput.current != null && searchInput.current.removeEventListener('keydown', handleSearchInputKeyDown);
    };
  }, []);

  // 🚀 PERFORMANCE: Chrome extension check removed - already done in App.js
  // This was causing a duplicate fetch on every ProductSearch render

  return (
    <>
      {/* ImportProductByURL modal moved outside Page component for proper z-index layering */}
      {isShowImportURLComponent ? (
        <Suspense fallback={null}>
          <ImportProductByURL isShowImportURLComponent={isShowImportURLComponent} setIsShowImportURLComponent={setIsShowImportURLComponent} />
        </Suspense>
      ) : null}
      <Page title={i18("Product Search")} fullWidth secondaryActions={<Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <Popover
          active={countryActive}
          activator={countryActivator}
          autofocusTarget="first-node"
          onClose={toggleCountryActive}
          ariaHaspopup="menu"
          preferredAlignment="right"
        >
          <Box style={{ width: "145px" }}></Box>
          <Scrollable style={{ height: '300px' }} focusable>
            <Box style={{
              width: "170px",
              padding: "12px",
            }}>
              <TextField
                style={{ width: "100%" }}
                value={countryInput}
                onChange={handleCountryInputChange}
                placeholder={i18("Search")}
                autoComplete="off"
                prefix={<Icon
                  source={SearchIcon}
                  tone="base"
                />}
              />
              <OptionList
                onChange={(value) => {
                  if (value != null && value.length > 0 && value[0] !== currentCountry) {
                    setCurrentCountry(value[0].slice(3));
                    countryEffect(value[0].slice(0, 2));
                  }
                  handleCountryInputChange("");
                  toggleCountryActive();
                }}
                options={
                  countryList.map(item => {
                    if (item.option.toUpperCase().includes(countryInput.toUpperCase())) {
                      return { value: item.value + "-" + item.option, label: <Text variant="bodyMd">{translationCountry(item.option)}</Text> }
                    }
                  }).filter(res => res != null)
                }
                selected={[]}
              />
            </Box>
          </Scrollable>
        </Popover>
        <Popover
          active={currencyActive}
          activator={currencyActivator}
          autofocusTarget="first-node"
          onClose={toggleCurrencyActive}
          ariaHaspopup="menu"
          preferredAlignment="right"
        >
          <Box style={{ width: "145px" }}></Box>
          <Scrollable style={{ height: '300px' }} focusable>
            <Box style={{
              width: "145px",
              padding: "12px",
            }}>
              <TextField
                style={{ width: "100%" }}
                value={currencyInput}
                onChange={handleCurrenctInputChange}
                placeholder={i18("Search")}
                autoComplete="off"
                prefix={<Icon
                  source={SearchIcon}
                  tone="base"
                />}
              />
              <OptionList
                onChange={(value) => {
                  if (value != null && value.length > 0 && value[0] !== currentCurrency) {
                    currencyEffect(value[0]);
                  }
                  handleCurrenctInputChange("");
                  toggleCurrencyActive();
                }}
                options={
                  majorCurrencyList.map(item => {
                    if (item.option.toLowerCase().includes(currencyInput.toLowerCase())) {
                      return { value: item.value, label: <Text variant="bodyMd">{item.option}</Text> }
                    }
                  }).filter(res => res != null)
                }
                selected={[]}
              />
            </Box>
          </Scrollable>
        </Popover>
        <Button variant="primary" size="medium" onClick={() => {
          setIsShowImportURLComponent(true);
        }}>{i18("Import by URL")}</Button>
      </Box>}>
        <Box id="ProductSearch">
          {adminSupplierIntentNotice ? (
            <Box style={{ marginBottom: "12px" }}>
              <Banner
                tone="info"
                onDismiss={() => setAdminSupplierIntentNotice(false)}
              >
                <Text variant="bodyMd">
                  {i18("Shopify product context was not available. Importing here creates a new Cartifind product.")}
                </Text>
              </Banner>
            </Box>
          ) : null}
          {sidekickImportNotice ? (
            <Box style={{ marginBottom: "12px" }}>
              <Banner
                tone="info"
                action={sidekickSupplierUrl ? {
                  content: i18("Open supplier page"),
                  onAction: () => openExternalUrl(sidekickSupplierUrl),
                } : undefined}
                onDismiss={() => {
                  setSidekickImportNotice("");
                  setSidekickSupplierUrl("");
                }}
              >
                <Text variant="bodyMd">{sidekickImportNotice}</Text>
              </Banner>
            </Box>
          ) : null}
          {smartRecommendationSearchNotice ? (
            <Box style={{ marginBottom: "12px" }}>
              <Banner
                tone="info"
                onDismiss={() => setSmartRecommendationSearchNotice(false)}
              >
                <Text variant="bodyMd">
                  {i18("Opened from Smart recommendations. Review trend-backed products before importing.")}
                  {smartRecommendationProvider ? ` ${i18("Signal")}: ${smartRecommendationProvider}.` : ""}
                  {smartRecommendationCountry ? ` ${i18("Market")}: ${smartRecommendationCountry}.` : ""}
                </Text>
              </Banner>
            </Box>
          ) : null}
          <GetStartedGuide onOpenImportByURL={() => setIsShowImportURLComponent(true)} />
          <Box ref={searchInput} style={{ boxShadow: "var(--p-shadow-100)", background: "var(--p-color-bg-surface)", borderRadius: "var(--p-border-radius-300)", padding: "16px" }}>
            <TextField
              style={{ width: "100%" }}
              type="search"
              value={titleValue}
              onChange={handleTitleChange}
              onBlur={handleTitleBlur}
              prefix={<Icon
                source={SearchIcon}
                tone="base"
              />}
              focused={false}
              autoComplete="off"
              connectedRight={<Popover
                active={sortActive}
                activator={sortActivator}
                autofocusTarget="first-node"
                onClose={toggleSortActive}
                ariaHaspopup="menu"
                preferredAlignment="right"
              >
                <Box style={{ width: "150px" }}></Box>
                <OptionList
                  onChange={(value) => {
                    if (value != null && value.length > 0 && value[0] !== currentSort) {
                      setCurrentSort(value[0]);
                      setSortSelected(value);
                      if (value[0] === "Default") {
                        setCurrentSortRule("normal");
                      } else if (value[0].includes("Low to High")) {
                        setCurrentSortRule("growth");
                      } else {
                        setCurrentSortRule("decline");
                      }
                    }
                    toggleSortActive();
                  }}
                  options={[
                    { value: 'Default', label: <Text variant="bodyMd">{i18("Default")}</Text> },
                    { value: 'Price: Low to High', label: <Text variant="bodyMd">{i18("Price: Low to High")}</Text> },
                    { value: 'Price: High to Low', label: <Text variant="bodyMd">{i18("Price: High to Low")}</Text> },
                    { value: 'Sales: High to Low', label: <Text variant="bodyMd">{i18("Sales: High to Low")}</Text> },
                  ]}
                  selected={[]}
                />
                {/* </Text> */}
              </Popover>}
            />
            <Box style={{ paddingTop: "20px" }}>
              <InlineStack gap="100">
                <CascadingMenu
                  active={productCategoryActive}
                  activator={productCategoryActivator}
                  onClose={closeProductCategoryActive}
                  items={categoryList}
                  onChoose={chooseCategory}
                />
                <Popover
                  active={priceRangeActive}
                  activator={priceRangeActivator}
                  autofocusTarget="first-node"
                  onClose={priceRangeToggleActive}
                  ariaHaspopup="menu"
                  fixed={false}
                  preferredAlignment="left"
                >
                  <OptionList
                    onChange={(value) => {
                      if (value != null && value.length > 0 && value[0] !== currentPriceRange) {
                        setCurrentPriceRange(value[0].includes("Under") ? i18("Under") + value[0].replace("Under", "") : value[0]);
                        setPriceRangeSelected(value);
                      }
                      if (value != null && value.length > 0 && value[0] !== "custom") {
                        priceRangeToggleActive();
                      }
                    }}
                    options={[
                      {
                        value: `Under ${currencySymbol}10`, label: <Box style={{ width: "165px" }}>
                          <RadioButton
                            label={<Text variant="bodyMd">{`${i18("Under")} ${currencySymbol}10`}</Text>}
                            checked={priceRangeSelected.includes(`Under ${currencySymbol}10`)}
                            id="$10"
                          />
                        </Box>
                      },
                      {
                        value: `${currencySymbol}10 ~ ${currencySymbol}30`, label: <Box style={{ width: "165px" }}>
                          <RadioButton
                            label={<Text variant="bodyMd">{`${currencySymbol}10 ~ ${currencySymbol}30`}</Text>}
                            checked={priceRangeSelected.includes(`${currencySymbol}10 ~ ${currencySymbol}30`)}
                            id="$30"
                          />
                        </Box>
                      },
                      {
                        value: `${currencySymbol}30 ~ ${currencySymbol}50`, label: <Box style={{ width: "165px" }}>
                          <RadioButton
                            label={<Text variant="bodyMd">{`${currencySymbol}30 ~ ${currencySymbol}50`}</Text>}
                            checked={priceRangeSelected.includes(`${currencySymbol}30 ~ ${currencySymbol}50`)}
                            id="$50"
                          />
                        </Box>
                      },
                      {
                        value: `${currencySymbol}50 ~ ${currencySymbol}100`, label: <Box style={{ width: "165px" }}>
                          <RadioButton
                            label={<Text variant="bodyMd">{`${currencySymbol}50 ~ ${currencySymbol}100`}</Text>}
                            checked={priceRangeSelected.includes(`${currencySymbol}50 ~ ${currencySymbol}100`)}
                            id="$100"
                          />
                        </Box>
                      },
                      {
                        value: 'custom', label: <Box style={{ width: "165px" }}>
                          <RadioButton
                            label={
                              <Box>
                                <InlineStack gap="100" blockAlign="center">
                                  <Box width="54px">
                                    <TextField
                                      value={minPriceRange}
                                      placeholder={i18("Min")}
                                      onChange={handleMinPriceRangeChange}
                                      autoComplete="off"
                                      onBlur={handleMinPriceRangeBlur}
                                    /></Box>
                                  <Text variant="bodyMd">~</Text>
                                  <Box width="54px">
                                    <TextField
                                      value={maxPriceRange}
                                      placeholder={i18("Max")}
                                      onChange={handleMaxPriceRangeChange}
                                      autoComplete="off"
                                      onBlur={handleMaxPriceRangeBlur}
                                    /></Box>
                                </InlineStack>
                              </Box>
                            }
                            checked={priceRangeSelected.includes("custom")}
                            id="custom"
                          />
                        </Box>
                      },
                      {
                        value: 'clear', label: <Text variant="bodyMd">{i18("clear")}</Text>
                      },
                    ]}
                    selected={[]}
                  />
                </Popover>
              </InlineStack>
            </Box>
          </Box>
          <Box style={{ padding: "20px 0px" }} ref={productCardWrap}>
            <CardList
              cardWidth={cardWidth}
              rowsNumber={rowsNumber}
              currentCategoryValue={currentCategoryValue}
              currentSort={currentSort}
              minPriceRange={currentMinPriceRange}
              maxPriceRange={currentmaxPriceRange}
              currentPriceRange={currentPriceRange}
              currentTitle={currentTitle}
              currencySymbol={currencySymbol}
              setIsShowImportURLComponent={setIsShowImportURLComponent}
              onClickProductDetail={(productTitle, productLink) => {
                // Redirect directly to AliExpress since chrome extension detection
                // doesn't work in iframes (Shopify Admin context)
                if (sendLog != null) {
                  sendLog("PRODUCT_DETAIL_REDIRECT_PAGE_AUTO_REDIRECT");
                }
                openExternalUrl(productLink);
              }}
              isShowImportURLComponent={isShowImportURLComponent}
              hasChromeExtension={hasChromeExtension}
              currency={currentCurrency}
            />
          </Box>

        </Box>
      </Page>
    </>
  )
}

function isCartifindSupplierPage(value) {
  try {
    const url = new URL(String(value || ""));
    if (url.protocol !== "https:" && url.protocol !== "http:") return false;
    const host = url.hostname.toLowerCase();
    return [
      "aliexpress.",
      "temu.",
      "1688.com",
      "alibaba.com",
      "shein.",
      "etsy.com",
      "amazon.",
    ].some((part) => host.includes(part));
  } catch (_error) {
    return false;
  }
}

function PaginationButton(props) {
  const { pageIndex = 1, active = false, onClick } = props;
  return <Box style={{
    padding: " 4px 8px ",
    background: active ? "#E3E3E3" : "#0000000A",
    cursor: "pointer"
  }} onClick={onClick}>
    {/* <Button size="micro"> */}
    <Box style={{ color: active ? "#4A4A4A" : "#CCCCCC", minWidth: "12px" }}>
      <Text variant="headingSm" alignment="center">{pageIndex}</Text>
    </Box>
    {/* </Button> */}
  </Box>
}

export function PaginationButtonGroup(props) {
  const { currentPageIndex = 1, setPageIndex, maximum = 5 } = props;
  const [isShowFirstApostrophe, setIsShowFirstApostrophe] = useState(false);
  const [isShowSecondApostrophe, setIsShowSecondApostrophe] = useState(true);
  const [firstButtonIndex, setFirstButtonIndex] = useState(currentPageIndex);
  const [secondButtonIndex, setSecondButtonIndex] = useState(currentPageIndex + 1);
  const [thirdButtonIndex, setThirdButtonIndex] = useState(currentPageIndex + 2);
  const [fourthButtonIndex, setFourthButtonIndex] = useState(currentPageIndex + 3);

  useEffect(() => {
    if (currentPageIndex <= maximum - 4) {
      setFirstButtonIndex(currentPageIndex);
      setSecondButtonIndex(currentPageIndex + 1);
      setThirdButtonIndex(currentPageIndex + 2);
      setFourthButtonIndex(currentPageIndex + 3);
      setIsShowSecondApostrophe(currentPageIndex + 3 < maximum - 1);
      setIsShowFirstApostrophe(currentPageIndex > 2);
    } else {
      const difference = 4 - (maximum - currentPageIndex);
      setFirstButtonIndex(currentPageIndex - difference);
      setSecondButtonIndex(currentPageIndex + 1 - difference);
      setThirdButtonIndex(currentPageIndex + 2 - difference);
      setFourthButtonIndex(currentPageIndex + 3 - difference);
      setIsShowSecondApostrophe(currentPageIndex + 3 - difference < maximum - 1);
      setIsShowFirstApostrophe(currentPageIndex - difference > 2);
    }
  }, [currentPageIndex]);

  if (maximum === 0) {
    const arr = new Array(maximum).fill(0);
    return <div style={{ color: "#ccc" }}>
      <InlineStack align="center" blockAlign="center" gap="400">
        <PaginationButton active={currentPageIndex == 1} pageIndex={1} onClick={() => {
          setPageIndex(1);
        }} />
      </InlineStack>
    </div>
  } else if (maximum <= 6) {
    const arr = new Array(maximum).fill(0);
    return <div style={{ color: "#ccc" }}>
      <InlineStack align="center" blockAlign="center" gap="400">
        {
          arr.map((_item, index) => {
            return <PaginationButton active={currentPageIndex == (index + 1)} pageIndex={index + 1} onClick={() => {
              setPageIndex(index + 1);
            }} key={index} />
          })
        }
      </InlineStack>
    </div>
  }
  return <div style={{ color: "#ccc" }}>
    <InlineStack align="center" blockAlign="center" gap="400">
      {firstButtonIndex !== 1 ? <PaginationButton active={currentPageIndex == 1} pageIndex={1} onClick={() => {
        setPageIndex(1)
      }} /> : null}
      {isShowFirstApostrophe ? <Text variant="headingSm">...</Text> : null}
      <PaginationButton active={currentPageIndex == firstButtonIndex} pageIndex={firstButtonIndex} onClick={() => {
        setPageIndex(firstButtonIndex)
      }} />
      <PaginationButton active={currentPageIndex == secondButtonIndex} pageIndex={secondButtonIndex} onClick={() => {
        setPageIndex(secondButtonIndex)
      }} />
      <PaginationButton active={currentPageIndex == thirdButtonIndex} pageIndex={thirdButtonIndex} onClick={() => {
        setPageIndex(thirdButtonIndex)
      }} />
      <PaginationButton active={currentPageIndex == fourthButtonIndex} pageIndex={fourthButtonIndex} onClick={() => {
        setPageIndex(fourthButtonIndex)
      }} />
      {isShowSecondApostrophe ? <Text variant="headingSm">...</Text> : null}
      {fourthButtonIndex !== maximum ? <PaginationButton active={currentPageIndex == maximum} pageIndex={maximum} onClick={() => {
        setPageIndex(maximum)
      }} /> : null}
    </InlineStack>
  </div>
}

function getCountryOption(country) {
  let countryOption = "United States";
  countryList.map(item => {
    if (country != null && item.value === country) {
      countryOption = item.option;
    }
  })
  return countryOption;
}

function getInitialWidthRowNumber() {
  let currentWidth = "270px";
  let currentRowsNumber = 5;
  const allWidth = window.innerWidth - 59;
  switch (true) {
    case allWidth > 1670:
      // 一排六张
      currentWidth = (allWidth - 50) / 6 - 1 + "px";
      currentRowsNumber = 6;
      break;
    case allWidth > 1390:
      // 一排五张
      currentWidth = (allWidth - 40) / 5 - 1 + "px";
      currentRowsNumber = 5;
      break;
    case allWidth > 1110:
      // 一排四张
      currentWidth = (allWidth - 30) / 4 - 1 + "px";
      currentRowsNumber = 4;
      break;
    case allWidth > 830:
      // 一排三张
      currentWidth = (allWidth - 20) / 3 - 1 + "px";
      currentRowsNumber = 3;
      break;
    case allWidth > 550:
      // 一排两张
      currentWidth = (allWidth - 10) / 2 - 1 + "px";
      currentRowsNumber = 2;
      break;
    case allWidth > 270:
      // 一排一张
      currentWidth = allWidth + "px";
      currentRowsNumber = 1;
      break;
  }
  return { currentWidth, currentRowsNumber }
}

const debounce = (func, delay) => {
  let timeout;
  return () => {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      func();
    }, delay);
  };
};

const isValidNumber = (value) => {
  // Check if the value is a valid integer or float, or empty
  return value === '' || /^-?\d*\.?\d*$/.test(value);
};
const formatNumber = (value) => {
  // Remove trailing dot if it's the last character
  if (value != null && value.length > 0 && value.endsWith('.')) {
    return value.slice(0, -1);
  }
  return value;
};

export default ProductSearch;
